import { ImpactType, OxfordCategory, ProjectType, ProjectSubType } from '@prisma/client';
import { PROJECT_SUBTYPES_BY_IMPACT_TYPE, PROJECT_TYPES_BY_IMPACT_TYPE } from './admin-projects';
import z from 'zod';

// Kebab-case
export const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export type ProjectTypeByImpactType = Record<ImpactType, readonly ProjectType[]>;
export type ProjectSubTypeByProjectType = Partial<Record<ProjectType, readonly ProjectSubType[]>>;

export const isProjectTypeValidForImpactType = (impactType: ImpactType, projectType: ProjectType): boolean =>
  (PROJECT_TYPES_BY_IMPACT_TYPE[impactType] as readonly ProjectType[]).includes(projectType);

export const isProjectSubTypeValidForImpactType = (
  impactType: ImpactType,
  projectType: ProjectType,
  projectSubType: ProjectSubType,
): boolean => {
  const subtypesByProjectType = PROJECT_SUBTYPES_BY_IMPACT_TYPE[impactType] as ProjectSubTypeByProjectType;
  const allowed = subtypesByProjectType[projectType];
  return allowed?.includes(projectSubType) ?? false;
};

export const isProjectSubTypeRequired = (impactType: ImpactType, projectType: ProjectType): boolean => {
  const subtypesByProjectType = PROJECT_SUBTYPES_BY_IMPACT_TYPE[impactType] as ProjectSubTypeByProjectType;
  const options = subtypesByProjectType[projectType];
  return (options?.length || 0) > 0;
};

export const isOxfordCategoryApplicable = (impactType: ImpactType): boolean =>
  impactType === ImpactType.CARBON_OFFSETS || impactType === ImpactType.CARBON_REMOVAL;

type ProjectFieldShape = {
  impactType?: ImpactType;
  projectType?: ProjectType;
  projectSubType?: ProjectSubType | null;
  oxfordCategory?: OxfordCategory | null;
};

const refineCombinationValidity = <T extends z.ZodType>(schema: T) =>
  schema.superRefine((value, ctx) => {
    const { impactType, projectType, projectSubType, oxfordCategory } = value as ProjectFieldShape;

    if (impactType && projectType && !isProjectTypeValidForImpactType(impactType, projectType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['projectType'],
        message: `projectType ${projectType} is not valid for impactType ${impactType}`,
      });
    }
    if (impactType && oxfordCategory && !isOxfordCategoryApplicable(impactType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['oxfordCategory'],
        message: `oxfordCategory is not valid for impactType ${impactType}`,
      });
    }
    if (
      impactType &&
      projectType &&
      projectSubType &&
      !isProjectSubTypeValidForImpactType(impactType, projectType, projectSubType)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['projectSubType'],
        message: `projectSubType ${projectSubType} is not valid for project type ${projectType} (impactType: ${impactType})`,
      });
    }
  });

const refineSubTypeRequired = <T extends z.ZodType>(schema: T) =>
  schema.superRefine((value, ctx) => {
    const { impactType, projectType, projectSubType } = value as ProjectFieldShape;

    if (impactType && projectType && isProjectSubTypeRequired(impactType, projectType) && !projectSubType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['projectSubType'],
        message: `projectSubType is required for project type ${projectType} (impactType: ${impactType})`,
      });
    }
  });

const refineOxfordCategoryRequired = <T extends z.ZodType>(schema: T) =>
  schema.superRefine((value, ctx) => {
    const { impactType, oxfordCategory } = value as ProjectFieldShape;

    if (impactType && isOxfordCategoryApplicable(impactType) && !oxfordCategory) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['oxfordCategory'],
        message: `oxfordCategory is required for impactType ${impactType}`,
      });
    }
  });

type ProjectTypeValidationStrictness = 'draft' | 'publish';

/**
 * Applies cross-field refinement for impactType/projectType/projectSubType/oxfordCategory with variable strictness:
 *
 * - `draft`: only checks provided combo validity
 * - `publish`: full validation
 */
export const applyProjectTypeRefinement = <T extends z.ZodType>(
  schema: T,
  strictness: ProjectTypeValidationStrictness,
) => {
  const withValidity = refineCombinationValidity(schema);
  return strictness === 'publish' ? refineOxfordCategoryRequired(refineSubTypeRequired(withValidity)) : withValidity;
};
